import React, { useEffect, useRef, useState } from "react";
import { get, isNil } from "lodash";
import i18n from "i18n/i18n";

import { Icons, Loader, Input } from "@texada/ui";

import {
  ASSET_TYPE,
  CurrencyFormat,
  ReduxState,
  SrmBoolean
} from "helpers/helper";
import { useRequest } from "helpers/hooks";
import { useRect } from "@reach/rect";

import "./DescriptionRenderer.scss";
import { useSelector } from "react-redux";

function customTransformAndEncode(input) {
  const symbolMap = {
    "@": "[at]",
    "#": "[hash]",
    $: "[dollar]",
    "%": "[percent]",
    "&": "[and]",
    "-": "[hyphen]"
  };
  const transformed = input.replace(/[@#$%&]/g, match => symbolMap[match]);
  return encodeURIComponent(transformed);
}

interface IDescriptionRenderer {
  item: any;
  itemKey: string;
  readonly?: boolean;
  fetchEditable?: boolean;
  isEditable?: boolean;
  showTooltipOverride?: boolean;
  onChange?:
    | ((item: any, value: string) => any)
    | ((params: any, value: string) => void);
  collidesWithBottom?: boolean;
  customTooltipStyle?: ({
    style,
    collidesWithBottom,
    is300pxAboveBottom
  }) => any;
}

export const DescriptionRenderer: React.FC<IDescriptionRenderer> = props => {
  const {
    item,
    itemKey,
    readonly,
    fetchEditable,
    onChange,
    showTooltipOverride = false,
    collidesWithBottom,
    customTooltipStyle
  } = props;
  const vendor_product_number = useSelector<ReduxState, any>(
    state => state?.auth?.user?.companyFeatures?.vendor_product_number
  );
  const [isHover, setIsHover] = useState(false);
  const [style, setStyle] = useState({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
    x: 0,
    y: 0
  });
  const [detail, setDetail] = useState<any>(null);
  const ref = useRef<any>(null);
  const rect: any = useRect(ref) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [getProduct] = useRequest({ flask: true });
  const [value, setValue] = useState<string>("");
  const [isEditable, setIsEditable] = useState(props.isEditable || false);
  const productNumber = item?.product || item?.prod_n || item?.productNumber;
  const productClass = detail?.productClass || item?.productClass;
  const productGroup = detail?.productGroup || item?.productGroup;
  const modelNumber = detail?.modelNumber || item?.modelNumber;
  const make = detail?.make || item?.make;
  const statusCode = detail?.statusCode || item?.statusCode;
  const listPrice = detail?.listPrice || item?.listPrice;
  const vendorProductNumber =
    detail?.vendorProductNumber || item?.vendorProductNumber;
  const inventory = detail?.inventory || item?.inventory;
  const externalResourceUrl =
    detail?.externalResourceUrl || item?.externalResourceUrl;
  const barcodes = detail?.barcodes || item?.barcodes;
  const notes = detail?.notes || item?.notes;
  const itemType = item?.type ? item?.type : ASSET_TYPE.Sales;

  const getUrl = type => {
    if (type === ASSET_TYPE.Sales) {
      return `${localStorage.flask_url}/parts/${customTransformAndEncode(
        productNumber
      )}`;
    }
    return `${localStorage.flask_url}/products/${customTransformAndEncode(
      productNumber
    )}`;
  };

  useEffect(() => {
    const callApi = async () => {
      try {
        setIsLoading(true);
        const response = await getProduct(getUrl(itemType));
        setDetail(response?.data);
        setIsEditable(
          response?.data?.isDescriptionOverrideByMobile === SrmBoolean.True ||
            props.isEditable
        );
        setIsLoading(false);
      } catch (error) {
        if (itemType === ASSET_TYPE.Sales) {
          try {
            const response = await getProduct(getUrl(ASSET_TYPE.Rental));
            setDetail(response?.data);
            setIsEditable(
              response?.data?.isDescriptionOverrideByMobile ===
                SrmBoolean.True || props.isEditable
            );
            setIsLoading(false);
          } catch (error) {
            console.log(error?.response);
            setIsLoading(false);
            setIsEditable(false);
          }
        }
      }
    };

    if (fetchEditable) {
      callApi();
      return;
    }
    if (!isHover || !isNil(detail)) return;
    callApi();
  }, [isHover]);

  useEffect(() => {
    setValue(item[itemKey]);
  }, [item[itemKey]]);

  const is300pxAboveBottom = rect.bottom <= window.innerHeight - 300;

  const tooltipContent =
    vendor_product_number || showTooltipOverride ? (
      <div
        onMouseEnter={() => {
          setStyle(rect);
          setIsHover(true);
        }}
        onMouseLeave={() => setIsHover(false)}
        onClick={() =>
          window.open(
            `/inventory/${
              itemType === ASSET_TYPE.Rental ? "asset" : "part"
            }/${productNumber.trim()}`,
            "_blank"
          )
        }
      >
        <span className="info" ref={ref}>
          <Icons.Info />
        </span>
        <div
          className="line-item-detail"
          style={{
            top: style.top,
            left: style.left + 30,
            ...(is300pxAboveBottom
              ? {}
              : {
                  transform: "translateY(-100%)"
                }),
            display: isHover ? "grid" : "none",
            ...(customTooltipStyle
              ? customTooltipStyle({
                  style,
                  is300pxAboveBottom,
                  collidesWithBottom
                })
              : {})
          }}
        >
          <p>
            <strong>{productNumber}</strong>
          </p>
          <div>
            <p>{item.description}</p>
            {isLoading ? (
              <div className="loader-section">
                <div>
                  {i18n.t("loading")}
                  <div>
                    <Loader isLoading={isLoading} />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p>
                  <strong>{i18n.t("class")}: </strong>
                  {productClass}
                </p>
                <p>
                  <strong>{i18n.t("group")}: </strong>
                  {productGroup}
                </p>
                <p>
                  <strong>{i18n.t("model")}: </strong>
                  {modelNumber}
                </p>
                <p>
                  <strong>{i18n.t("make")}: </strong>
                  {make}
                </p>
                {detail?.hasOwnProperty("statusCode") && (
                  <p>
                    <strong>{i18n.t("asset_status")}: </strong>
                    {statusCode}
                  </p>
                )}
                <p>
                  <strong>{i18n.t("list_price")}: </strong>
                  {listPrice ? CurrencyFormat.format(listPrice) : ""}
                </p>
                <p>
                  <strong>{i18n.t("vendor_product_number")}: </strong>
                  {vendorProductNumber}
                </p>
                <p>
                  <strong>{i18n.t("include_inventory")}: </strong>
                  {inventory}
                </p>
                <p>
                  <strong>{i18n.t("external_resource_url")}: </strong>
                  {externalResourceUrl}
                </p>
                <p>
                  <strong>{i18n.t("barcodes")}: </strong>
                  {(barcodes || []).map(e => e).join(", ")}
                </p>
                <p>
                  <strong>{i18n.t("comments")}: </strong>
                  {Array.isArray(notes)
                    ? (notes || []).map((e, index) => (
                        <span key={index}>
                          {e.note}
                          <br />
                        </span>
                      ))
                    : notes}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    ) : (
      <></>
    );

  return (
    <div className="common-product-description-renderer">
      {readonly || !isEditable ? (
        <p className="clickable-text" tabIndex={0}>
          {get(item, itemKey, "")}
        </p>
      ) : (
        <Input
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={async e => {
            if (item.isLoading) return;
            setValue(item[itemKey]);
            const updated = await onChange(item, value);
            if (!updated) {
              setValue(item[itemKey]);
            }
          }}
          disabled={readonly || !isEditable}
          style={{ width: "80%" }}
        />
      )}
      {tooltipContent}
    </div>
  );
};

export default DescriptionRenderer;
